import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function channelBadgeClass(channel) {
  const c = (channel || "").toLowerCase();
  if (c === "email") return "bg-sky-100 text-sky-800 border border-sky-200";
  if (c === "sms") return "bg-emerald-100 text-emerald-800 border border-emerald-200";
  if (c === "whatsapp") return "bg-green-100 text-green-800 border border-green-200";
  return "bg-neutral-100 text-neutral-700 border border-neutral-200";
}

export default function EditConnectionDialog({ connection, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    mysaas_api_key: "",
    business_id: connection.business_id || "",
    location_id: connection.location_id || "",
    template_id: connection.template_id || "",
    template_name: connection.template_name || "",
    template_channel: connection.template_channel || "",
  });
  const [mapping, setMapping] = useState({
    name_col: connection.mapping?.name_col ?? 0,
    email_col: connection.mapping?.email_col ?? 1,
    phone_col: connection.mapping?.phone_col ?? 2,
    header_row: connection.mapping?.header_row ?? true,
    trigger_col: connection.mapping?.trigger_col ?? null,
    trigger_value: connection.mapping?.trigger_value || "",
  });
  const [locations, setLocations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [saving, setSaving] = useState(false);

  const verify = async () => {
    if (!form.mysaas_api_key || !form.business_id) {
      toast.error("API key and business ID required");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/mysaas/verify", {
        api_key: form.mysaas_api_key,
        business_id: form.business_id,
      });
      setLocations(data.locations || []);
      setVerified(true);
      toast.success(`Verified: ${data.business_name}`);
    } catch (e) {
      setLocations([]);
      setVerified(false);
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  // Load templates when location chosen
  useEffect(() => {
    if (!verified || !form.location_id || !form.mysaas_api_key) return;
    api.post("/mysaas/templates", {
      api_key: form.mysaas_api_key,
      business_id: form.business_id,
      location_id: form.location_id,
    })
      .then((r) => setTemplates(r.data.templates || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load templates"));
  }, [verified, form.location_id, form.business_id, form.mysaas_api_key]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        business_id: form.business_id,
        location_id: form.location_id,
        template_id: form.template_id,
        template_name: form.template_name || null,
        template_channel: form.template_channel || null,
        mapping: {
          ...mapping,
          trigger_col: mapping.trigger_col === null || mapping.trigger_col === "none" ? null : Number(mapping.trigger_col),
          trigger_value: mapping.trigger_value || null,
        },
      };
      if (form.mysaas_api_key) patch.mysaas_api_key = form.mysaas_api_key;
      await api.patch(`/connections/${connection.connection_id}`, patch);
      toast.success("Connection updated");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`edit-${connection.connection_id}`}
          className="p-2 hover:bg-neutral-100 rounded transition-colors"
          title="Edit credentials & mapping"
        >
          <PencilSimple weight="bold" size={15} />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Edit connection</DialogTitle>
          <DialogDescription>
            Update MySaaS credentials, template, column mapping and trigger. Leave API key blank to keep the current one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* MySaaS creds */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">MySaaS API key</Label>
              <Input
                data-testid="edit-api-key"
                type="password"
                value={form.mysaas_api_key}
                onChange={(e) => { setForm({ ...form, mysaas_api_key: e.target.value }); setVerified(false); }}
                placeholder="Leave blank to keep existing key"
                className="h-10 font-mono-tech"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Business ID</Label>
              <Input
                data-testid="edit-business-id"
                value={form.business_id}
                onChange={(e) => { setForm({ ...form, business_id: e.target.value, location_id: "" }); setVerified(false); }}
                className="h-10 font-mono-tech"
              />
            </div>
          </div>

          <Button
            data-testid="edit-verify-btn"
            onClick={verify}
            disabled={verifying || !form.mysaas_api_key || !form.business_id}
            variant="outline"
            className="h-10 gap-2"
          >
            <MagnifyingGlass weight="bold" size={16} /> {verifying ? "Verifying…" : "Verify with MySaaS"}
          </Button>

          {verified && (
            <>
              <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                <CheckCircle weight="fill" size={16} /> Verified — {locations.length} location(s)
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Location</Label>
                <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v, template_id: "" })}>
                  <SelectTrigger data-testid="edit-location-select" className="h-10">
                    <SelectValue placeholder="Choose a location…" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} · <span className="font-mono-tech text-xs">{loc.id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Template</Label>
                <Select
                  value={form.template_id}
                  onValueChange={(v) => {
                    const t = templates.find((x) => x.id === v);
                    setForm({ ...form, template_id: v, template_name: t?.name || "", template_channel: t?.channel || "" });
                  }}
                  disabled={templates.length === 0}
                >
                  <SelectTrigger data-testid="edit-template-select" className="h-10">
                    <SelectValue placeholder={templates.length === 0 ? "Pick a location first" : "Choose a template…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="inline-flex items-center gap-2">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${channelBadgeClass(t.channel)}`}>
                            {t.channel || "?"}
                          </span>
                          {t.name || t.id}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Column mapping */}
          <div className="border-t border-stone-200 pt-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Column mapping</div>
            <div className="grid grid-cols-3 gap-3">
              <ColSelect label="Name col" value={mapping.name_col} onChange={(v) => setMapping({ ...mapping, name_col: v })} testid="edit-map-name" />
              <ColSelect label="Email col" value={mapping.email_col} onChange={(v) => setMapping({ ...mapping, email_col: v })} testid="edit-map-email" />
              <ColSelect label="Phone col" value={mapping.phone_col} onChange={(v) => setMapping({ ...mapping, phone_col: v })} testid="edit-map-phone" />
            </div>
          </div>

          {/* Trigger */}
          <div className="border-t border-stone-200 pt-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Trigger column <span className="text-neutral-400 normal-case">(optional)</span></div>
            <div className="text-xs text-neutral-500 mb-3">Only fire when this column is filled (e.g. Service Date). Blank = fire on any new row.</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Column</Label>
                <Select
                  value={mapping.trigger_col === null || mapping.trigger_col === undefined ? "none" : String(mapping.trigger_col)}
                  onValueChange={(v) => setMapping({ ...mapping, trigger_col: v === "none" ? null : parseInt(v, 10) })}
                >
                  <SelectTrigger data-testid="edit-trigger-col-select" className="h-10 font-mono-tech">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <SelectItem key={i} value={String(i)}>{String.fromCharCode(65 + i)} · col {i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Must equal</Label>
                <Input
                  data-testid="edit-trigger-value"
                  value={mapping.trigger_value || ""}
                  onChange={(e) => setMapping({ ...mapping, trigger_value: e.target.value })}
                  placeholder="blank = non-empty"
                  className="h-10 font-mono-tech"
                  disabled={mapping.trigger_col === null || mapping.trigger_col === undefined}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid="edit-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#F58021] hover:bg-[#D6691A] text-white"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColSelect({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">{label}</Label>
      <Select value={String(value)} onValueChange={(v) => onChange(parseInt(v, 10))}>
        <SelectTrigger data-testid={testid} className="h-10 font-mono-tech">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 12 }).map((_, i) => (
            <SelectItem key={i} value={String(i)}>{String.fromCharCode(65 + i)} · col {i}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
